import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Loader } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FlowPageInterface } from "@/interfaces";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { useUser } from "@/contexts/UserContext.tsx";
import { useContract } from "@/hooks/useContract";
import { formatCurrency } from "@/utils/currency";
import { ContactUs } from "@/components/ContactUs.tsx";
import { AddCardForm } from "@/app/_checkout";
import type { RawGatewayConfig } from "@/app/_checkout/types";
import SavedCardList from "./saved-card-list";
import { usePaymentMethodsService } from "../services/use-payment-methods-service";

const FlowPageCaptureCard: FC<FlowPageInterface> = (props) => {
  const { t, i18n } = useTranslation(["capture-card"]);
  const { contractId } = useFlow();
  const { user } = useUser();
  const { data: contract } = useContract(contractId);

  const [saved, setSaved] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [gatewayError, setGatewayError] = useState<string | null>(null);

  const {
    data: paymentMethods,
    isLoading: methodsLoading,
    refetch: refetchMethods,
  } = usePaymentMethodsService(contractId ?? undefined);

  if (!props.currentlyVisible || !contract || !contractId) return <></>;

  const methods = paymentMethods ?? [];
  const hasCardOnFile = methods.length > 0;

  const gatewayConfig = contract.meta?.gateway_config as
    | RawGatewayConfig
    | undefined;
  const company = contract.relationships?.company?.data;
  const services = contract.relationships?.belongsToManyServices?.data;

  // Same untyped user attributes as flow.tsx (see its TODO)
  const attributes = (user?.attributes ?? {}) as {
    client_firstname?: string;
    client_lastname?: string;
    client_email?: string;
  };
  const billingInfo = {
    first_name: attributes.client_firstname ?? "",
    last_name: attributes.client_lastname ?? "",
    email: attributes.client_email ?? "",
    country: "CA",
    address: contract.relationships?.address?.data?.attributes?.address ?? "",
  };

  return (
    <div className="grid lg:grid-cols-3 grid-cols-1 gap-9">
      <div className="col-span-1 md:col-span-2">
        <div className="follo-card">
          <div className="grid gap-3 mx-8 my-7">
            {methodsLoading ? (
              <div className="flex justify-center py-10">
                <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : hasCardOnFile && !addingNew ? (
              <section>
                {/* Animated "you're all set" hero */}
                <div className="flex flex-col items-center text-center pt-4 pb-8">
                  <div className="relative mb-5">
                    <motion.span
                      className="absolute inset-0 rounded-full bg-green-500/30"
                      initial={{ scale: 1, opacity: 0.6 }}
                      animate={{ scale: 2.1, opacity: 0 }}
                      transition={{ duration: 1.1, ease: "easeOut", delay: 0.35 }}
                    />
                    <motion.div
                      className="relative flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-600/25"
                      initial={{ scale: 0, rotate: -30 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    >
                      <Check className="size-8" strokeWidth={3} />
                    </motion.div>
                  </div>
                  <motion.h3
                    className="text-xl font-semibold text-card-foreground"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    {saved
                      ? t(
                          "capture-card:capture-card-all-set-saved-title",
                          "Card saved — you're all set!",
                        )
                      : t(
                          "capture-card:capture-card-all-set-title",
                          "You're all set!",
                        )}
                  </motion.h3>
                  <motion.p
                    className="mt-2 max-w-md text-sm text-muted-foreground"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    {t(
                      "capture-card:capture-card-all-set-description",
                      "Your payment method is securely on file — nothing has been charged.",
                    )}
                  </motion.p>
                </div>

                {gatewayError && (
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{gatewayError}</span>
                  </div>
                )}

                <SavedCardList contractId={contractId} methods={methods} />

                <div className="mt-6 flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setGatewayError(null);
                      setAddingNew(true);
                    }}
                  >
                    {t("capture-card:capture-card-add-another", "Add another card")}
                  </Button>
                  <Button type="button" onClick={props.nextStep}>
                    {t("capture-card:capture-card-next", "Next")}
                  </Button>
                </div>
              </section>
            ) : (
              <section>
                <h3 className="follo-label mb-2">
                  {t("capture-card:capture-card-title", "Save a card on file")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t(
                    "capture-card:capture-card-description",
                    "Your contract only includes on-demand services. Save a card on file so we can bill you as the work is done. Nothing will be charged now.",
                  )}
                </p>
                {gatewayError && (
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{gatewayError}</span>
                  </div>
                )}
                {gatewayConfig ? (
                  <>
                    <AddCardForm
                      contractId={contractId}
                      gatewayConfig={gatewayConfig}
                      billingInfo={billingInfo}
                      submitLabel={t(
                        "capture-card:capture-card-save-button",
                        "Save card",
                      )}
                      onSuccess={() => {
                        setGatewayError(null);
                        setSaved(true);
                        setAddingNew(false);
                        refetchMethods();
                      }}
                      onError={setGatewayError}
                    />
                    {hasCardOnFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="mt-2 w-full"
                        onClick={() => {
                          setGatewayError(null);
                          setAddingNew(false);
                        }}
                      >
                        {t("capture-card:capture-card-cancel", "Cancel")}
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {t(
                        "capture-card:capture-card-gateway-unavailable",
                        "The payment gateway is currently unavailable. Please try again later or contact us.",
                      )}
                    </span>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
        <ContactUs email={company?.attributes.email} />
      </div>
      <div className="col-span-1">
        <div className="follo-card px-8 py-7">
          <section>
            <h3 className="follo-label mb-4.5">
              {t("capture-card:capture-card-summary-title", "Summary")}
            </h3>
            {services?.map((service, index) => (
              <div
                key={index + "_" + service.id}
                className="flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
              >
                <div>{service.attributes.name}</div>
                <div>
                  {formatCurrency(service.meta.pivot.price ?? 0, i18n.language)}
                </div>
              </div>
            ))}
            <Separator className="mt-4 mb-5" />
            <div className="flex justify-between text-sm leading-5 font-medium text-primary pb-4">
              <div className="font-semibold">
                {t("capture-card:capture-card-due-now", "Due now")}
              </div>
              <div className="text-base leading-6 font-semibold">
                {formatCurrency(0, i18n.language)}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                "capture-card:capture-card-on-demand-note",
                "On-demand services are billed after each visit.",
              )}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FlowPageCaptureCard;
